import React from "react";
import type { ModeRendererProps } from "./types";

export function VasarelyNeuralinkMode({ brand, today, Copy }: ModeRendererProps) {
  // Neuralink gradient: neural purple to interface silver
  const neuralinkGradient = (
    <defs>
      <linearGradient id="neuralinkGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#6A1B9A" />
        <stop offset="50%" stopColor="#9C27B0" />
        <stop offset="100%" stopColor="#BDBDBD" />
      </linearGradient>
      <radialGradient id="neuralGlow" cx="50%" cy="50%">
        <stop offset="0%" stopColor="#9C27B0" stopOpacity={0.6} />
        <stop offset="100%" stopColor="#6A1B9A" stopOpacity={0} />
      </radialGradient>
    </defs>
  );

  // Spike rasters: neural firing patterns
  const spikeRasters: JSX.Element[] = [];
  for (let i = 0; i < 32; i++) {
    const y = 100 + i * 29;
    for (let j = 0; j < 40; j++) {
      const x = 60 + j * 17;
      const fires = Math.random() > 0.85;
      if (fires) {
        spikeRasters.push(
          <line
            key={`spike-${i}-${j}`}
            x1={x}
            y1={y - 8}
            x2={x}
            y2={y + 8}
            stroke="#9C27B0"
            strokeWidth={1.5}
            opacity={0.6}
          />
        );
      }
    }
  }

  // EEG meshes: brainwave topography
  const eegMeshes: JSX.Element[] = [];
  for (let i = 0; i < 14; i++) {
    const y = 120 + i * 65;
    const points = [];
    for (let j = 0; j < 13; j++) {
      const x = 70 + j * 55;
      const alpha = Math.sin(j * 0.8 + i * 0.3) * 25;
      const beta = Math.cos(j * 1.2 + i * 0.5) * 15;
      points.push(`${x},${y + alpha + beta}`);
    }
    eegMeshes.push(
      <polyline
        key={`eeg-${i}`}
        points={points.join(" ")}
        fill="none"
        stroke="url(#neuralinkGrad)"
        strokeWidth={1.5}
        opacity={0.4}
      />
    );
  }

  // Coherence maps: neural synchronization
  const coherenceMaps: JSX.Element[] = [];
  for (let i = 0; i < 12; i++) {
    const angle = (i * Math.PI * 2) / 12;
    const radius = 180;
    const x1 = 397 + radius * Math.cos(angle);
    const y1 = 561.5 + radius * Math.sin(angle);
    for (let j = i + 1; j < 12; j++) {
      const angle2 = (j * Math.PI * 2) / 12;
      const x2 = 397 + radius * Math.cos(angle2);
      const y2 = 561.5 + radius * Math.sin(angle2);
      const coherence = Math.random();
      if (coherence > 0.6) {
        coherenceMaps.push(
          <line
            key={`coh-${i}-${j}`}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke="url(#neuralinkGrad)"
            strokeWidth={coherence * 2}
            opacity={coherence * 0.4}
          />
        );
      }
    }
  }

  // Electrode grid: implant interface
  const electrodeGrid: JSX.Element[] = [];
  for (let row = 0; row < 12; row++) {
    for (let col = 0; col < 12; col++) {
      const angle = (row * Math.PI * 2) / 12;
      const radius = 80 + col * 12;
      const x = 397 + radius * Math.cos(angle);
      const y = 561.5 + radius * Math.sin(angle);
      electrodeGrid.push(
        <circle
          key={`electrode-${row}-${col}`}
          cx={x}
          cy={y}
          r={3}
          fill="#BDBDBD"
          opacity={0.5}
        />
      );
    }
  }

  // Central neural interface
  const neuralCore = (
    <g opacity={0.9}>
      <circle cx={397} cy={561.5} r={110} fill="url(#neuralGlow)" />
      <circle cx={397} cy={561.5} r={75} fill="none" stroke="#9C27B0" strokeWidth={3} />
      <circle cx={397} cy={561.5} r={55} fill="none" stroke="#BDBDBD" strokeWidth={2} />
      <circle cx={397} cy={561.5} r={35} fill="none" stroke="#6A1B9A" strokeWidth={2} />
      <text
        x={397}
        y={575}
        fontFamily="monospace"
        fontSize={24}
        fontWeight="bold"
        fill="#FFFFFF"
        textAnchor="middle"
      >
        N1
      </text>
    </g>
  );

  return (
    <g>
      {neuralinkGradient}
      {spikeRasters}
      {eegMeshes}
      {electrodeGrid}
      {coherenceMaps}
      {neuralCore}
      <g className="mode-copy" transform="translate(48, 840)" fill="#FFFFFF" fontFamily="monospace">
        <text x={0} y={0} fontSize={18} fontWeight="bold">{Copy?.title}</text>
        <text x={0} y={22} fontSize={14}>{Copy?.subtitle}</text>
        <text x={0} y={44} fontSize={12} opacity={0.85}>{Copy?.date}</text>
      </g>
    </g>
  );
}
